import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { trapFocus } from '@theme/focus';

// Horizon owns normal mutations and morphs. All listeners survive hydration/editor replacements.
const pending = new Set();
const quantityIntents = new Set();
const disabledByBridge = new Map();
const drawer = () => document.getElementById('cart-drawer');
const panel = () => drawer()?.querySelector('.gb-cart');
const component = () => drawer()?.querySelector('cart-items-component');
const busy = () => pending.size > 0 || quantityIntents.size > 0 || CartItemsComponent.pendingQuantityUpdates.length > 0;
const frame = () => new Promise((resolve) => requestAnimationFrame(resolve));
let needsRefresh = false;
let errorMessage = '';
let adding = false;
let refreshPromise;
let lockedPanel;
let refreshRequested = false;
let observedSectionId;
const desktopQuery = matchMedia('(min-width: 990px)');
let addedMessage = '';
const highlights = new Map();
let feedbackKeys = [];
let undoState = [];
let undoPending;
let undoTimer;
let campaignTimer;
let campaignBoundary;
const redoFixes = new Map();
let redoFixing = false;
let checkingOut = false;
let checkoutTimer;

// Match variant, plan and all properties: two customized lines must never merge in undo.
const lineSignature = (item) => JSON.stringify([
  String(item.variant_id || item.id),
  String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ''),
  Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
  item.parent_relationship?.parent_key || item.parent_relationship?.parent?.key || '',
]);
const matchingQuantity = (cart, signature) => (cart.items || []).filter((item) => lineSignature(item) === signature)
  .reduce((quantity, item) => quantity + Number(item.quantity || 0), 0);

function hideUndo() {
  const focusedUndo = document.activeElement?.matches?.('[data-gb-cart-undo-button]');
  clearTimeout(undoTimer);
  undoTimer = undefined;
  undoState = [];
  syncUndo();
  if (focusedUndo && drawer()?.isOpen) {
    (panel()?.querySelector('.gb-cart__quantity input') || panel()?.querySelector('.gb-cart__close'))?.focus({ preventScroll: true });
    retrap();
  }
}
function syncUndo() {
  const box = panel()?.querySelector('[data-gb-cart-undo]');
  if (!box) return;
  box.hidden = undoState.length === 0;
  if (box.hidden) return;
  const names = [...new Set(undoState.map((record) => record.name))].join(', ');
  const label = (box.dataset.removed || '').replace('[product]', names);
  const text = box.querySelector('[data-gb-cart-undo-text]');
  if (text && text.textContent !== label) text.textContent = label;
}
function showUndo(records) {
  clearTimeout(undoTimer);
  undoState = records;
  syncUndo();
  undoTimer = setTimeout(hideUndo, 6000);
}
function prepareRemoval(cart, lines) {
  hideUndo();
  const records = new Map();
  for (const item of lines) {
    const id = item.variant_id || item.id;
    const quantity = Number(item.quantity);
    const hasChildren = (cart.items || []).some((child) =>
      (child.parent_relationship?.parent_key || child.parent_relationship?.parent?.key) === item.key);
    if (!id || !Number.isSafeInteger(quantity) || quantity < 1 || item.parent_relationship || hasChildren) continue;
    const signature = lineSignature(item);
    const existing = records.get(signature);
    if (existing) { existing.payload.quantity += quantity; continue; }
    const payload = { id, quantity };
    if (item.properties && Object.keys(item.properties).length) payload.properties = structuredClone(item.properties);
    const plan = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan;
    if (plan) payload.selling_plan = plan;
    records.set(signature, { signature, payload, before: matchingQuantity(cart, signature), name: item.product_title || item.title || '' });
  }
  undoPending = { kind: 'remove', records: [...records.values()] };
}
function settleUndo(cart) {
  if (!undoPending) return;
  const operation = undoPending;
  undoPending = undefined;
  const records = operation.records.map((record) => {
    const current = matchingQuantity(cart, record.signature);
    const difference = operation.kind === 'remove' ? record.before - current : current - record.before;
    const applied = Math.max(0, Math.min(record.payload.quantity, difference));
    const quantity = operation.kind === 'remove' ? applied : record.payload.quantity - applied;
    return { ...record, payload: { ...record.payload, quantity } };
  }).filter((record) => record.payload.quantity > 0);
  if (records.length) showUndo(records);
  else {
    hideUndo();
    if (operation.kind === 'restore') {
      const names = [...new Set(operation.records.map((record) => record.name))].join(', ');
      addedMessage = (panel()?.querySelector('[data-gb-cart-undo]')?.dataset.restored || '').replace('[product]', names);
    }
  }
}
function captureRemoval(event) {
  if (event.action !== 'remove' || !event.target.matches?.('cart-items-component') || !drawer()?.contains(event.target)) return;
  const items = [];
  for (const node of event.target.querySelectorAll('[data-gb-cart-line]')) {
    try {
      const item = JSON.parse(node.dataset.json);
      // Liquid's `properties | json` writes [[key, value], ...]; /cart/add.js wants an object (else 400 "expected Array to
      // be a Hash: properties", so Undo never restored a bundle), and lineSignature must match /cart.js lines.
      if (Array.isArray(item.properties)) item.properties = Object.fromEntries(item.properties);
      items.push(item);
    } catch { /* A malformed snapshot must not invent a restore. */ }
  }
  const keys = new Set((event.lines || []).filter((line) => line.quantity === 0).map((line) => line.id));
  prepareRemoval({ items }, items.filter((item) => keys.has(item.key)));
}

// Only the optional end text is dated; the campaign name stays visible, as in 13.75.
function campaignWindow(fromText = '', untilText = '', now = Date.now()) {
  fromText = fromText.trim(); untilText = untilText.trim();
  const from = Date.parse(fromText), until = Date.parse(untilText);
  const visible = (!fromText || (Number.isFinite(from) && now >= from)) &&
    (!untilText || (Number.isFinite(until) && now < until));
  const next = Math.min(...[from, until].filter((boundary) => Number.isFinite(boundary) && boundary > now));
  return { visible, next };
}
function syncCampaign() {
  let next = Infinity;
  for (const end of panel()?.querySelectorAll('[data-gb-cart-campaign-end]') || []) {
    const state = campaignWindow(end.dataset.showFrom, end.dataset.hideAfter);
    end.hidden = !state.visible;
    next = Math.min(next, state.next);
  }
  if (next === campaignBoundary) return;
  clearTimeout(campaignTimer);
  campaignBoundary = next;
  campaignTimer = Number.isFinite(next) ? setTimeout(() => {
    campaignBoundary = undefined;
    syncCampaign();
  }, Math.min(next - Date.now() + 1, 2147483647)) : undefined;
}


function syncControls() {
  const cart = panel();
  if (!cart) return;
  const updating = busy() || adding;
  cart.classList.toggle('is-updating', updating);
  cart.setAttribute('aria-busy', String(updating));
  const form = cart.querySelector('[data-gb-cart-form]');
  form?.setAttribute('aria-busy', String(updating));
  let restoredControls = false;
  for (const button of cart.querySelectorAll('[data-gb-cart-checkout], [data-gb-cart-upsell], [data-gb-cart-redo-input], .gb-cart__quantity button, .gb-cart__quantity input, .gb-cart__remove, [data-gb-cart-undo-button]')) {
    const disable = updating || (button.matches('[data-gb-cart-checkout], [data-gb-cart-undo-button]') && needsRefresh);
    if (disable) {
      if (!disabledByBridge.has(button)) disabledByBridge.set(button, button.disabled);
      if (!button.disabled) button.disabled = true;
    } else if (disabledByBridge.has(button)) {
      const original = disabledByBridge.get(button);
      if (button.disabled !== original) button.disabled = original;
      disabledByBridge.delete(button);
      restoredControls = true;
    }
  }
  if (restoredControls && !updating) {
    for (const selector of cart.querySelectorAll('cart-quantity-selector-component')) selector.updateButtonStates?.();
  }
  for (const node of disabledByBridge.keys()) if (!node.isConnected) disabledByBridge.delete(node);
  const slot = cart.querySelector('[data-gb-cart-error]');
  if (slot) {
    slot.hidden = !errorMessage;
    const text = slot.querySelector('[data-gb-cart-error-message]');
    if (text && text.textContent !== errorMessage) text.textContent = errorMessage;
    const retry = slot.querySelector('[data-gb-cart-retry]');
    if (retry) { retry.hidden = !needsRefresh; if (retry.disabled !== updating) retry.disabled = updating; }
  }
  syncUndo();
  syncCampaign();
  if (!updating) {
    syncBubble();
    if (!needsRefresh) checkRedoFix(cart);
  }
  const status = cart.querySelector('[data-gb-cart-added-status]');
  if (status && status.textContent !== addedMessage) status.textContent = addedMessage;
  if (feedbackKeys.length) {
    const rows = [...cart.querySelectorAll('[ref="cartItemRows[]"]')].filter((row) => feedbackKeys.includes(row.dataset.key));
    if (rows.length) {
      feedbackKeys = [];
      cart.querySelector('.gb-cart__scroll')?.scrollTo({ top: 0 });
      for (const row of rows) {
        clearTimeout(highlights.get(row));
        row.classList.add('gb-cart__just-added');
        highlights.set(row, setTimeout(() => { row.classList.remove('gb-cart__just-added'); highlights.delete(row); }, 2200));
      }
    }
  }
}

// Horizon's bubble counts every cart line; the drawer's count leaves out the Checkout+ fee line.
function syncBubble() {
  const count = Number(panel()?.querySelector('[data-gb-cart-count]')?.dataset.gbCartCount);
  if (!Number.isInteger(count)) return;
  for (const icon of document.querySelectorAll('cart-icon')) {
    if (typeof icon.renderCartBubble !== 'function' || icon.currentCartCount === count) continue;
    icon.renderCartBubble(count, false);
  }
}

// Liquid asks for a fix when a fee line is in the cart (it goes; the choice is kept) or nothing returnable is left
// (only Imperfect boards, or an empty cart: the choice is cleared). Each fix is tried twice at most, so a refused
// update never loops.
function checkRedoFix(cart) {
  const node = cart.querySelector('[data-gb-cart-redo-fix]');
  if (!node || redoFixing || adding) return;
  const signature = node.dataset.updates || '';
  const tries = redoFixes.get(signature) || 0;
  if (tries >= 2) return;
  redoFixes.set(signature, tries + 1);
  redoFixing = true;
  queueMicrotask(() => fixRedo(signature));
}
async function fixRedo(signature) {
  adding = true;
  try {
    syncControls();
    await requestJSON(Theme.routes.cart_update_url, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(JSON.parse(signature)),
    });
    adding = false;
    await refreshCart();
  } catch (error) {
    adding = false;
    needsRefresh = true;
    errorMessage = error.message || defaultError();
    try { await refreshCart(); } catch { /* Retry remains available; checkout remains blocked. */ }
  } finally {
    adding = false;
    redoFixing = false;
    syncControls();
    flushRefreshRequest();
  }
}

function rememberCartFocus(event) {
  const control = document.activeElement;
  if (!(control instanceof HTMLElement) || !panel()?.contains(control)) return;
  const row = control.closest('[ref="cartItemRows[]"]');
  return {
    control, key: row?.dataset.key,
    variant: row?.dataset.variantId || event.target.dataset?.variantId || (undoPending?.kind === 'restore' ? String(undoPending.records[0]?.payload.id || '') : undefined),
    action: control.matches('input') ? 'input' : control.matches('[name=plus]') ? '[name=plus]' : '[name=minus]',
  };
}
function restoreCartFocus(focus) {
  if (!focus || !drawer()?.isOpen) return;
  const active = document.activeElement;
  if (active !== document.body && active !== focus.control && active?.isConnected && !active.closest?.('[hidden]')) return;
  if (focus.control.isConnected && !focus.control.closest('[hidden]') && !focus.control.disabled) return;
  const rows = [...(panel()?.querySelectorAll('[ref="cartItemRows[]"]') || [])];
  const row = rows.find((row) => row.dataset.key === focus.key) || rows.find((row) => row.dataset.variantId === focus.variant);
  const undo = panel()?.querySelector('[data-gb-cart-undo]:not([hidden]) button');
  const control = row?.querySelector(focus.action) || undo || rows[0]?.querySelector('input') || panel()?.querySelector('.gb-cart__close');
  if (control && !control.disabled) control.focus({ preventScroll: true });
}

function retrap() {
  const cart = panel();
  if (!cart || !drawer()?.isOpen || !desktopQuery.matches) return;
  const active = document.activeElement;
  trapFocus(cart);
  if (active instanceof HTMLElement && active.isConnected && cart.contains(active)) active.focus({ preventScroll: true });
}

function openCart() {
  const cartDrawer = drawer();
  if (cartDrawer && !cartDrawer.isOpen) cartDrawer.open();
}

async function requestJSON(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || data.errors || defaultError());
    return data;
  } catch (error) {
    if (error.name === 'AbortError') throw new Error(panel()?.querySelector('[data-gb-cart-error]')?.dataset.timeout || defaultError());
    throw error;
  } finally { clearTimeout(timer); }
}
async function cartData() {
  return requestJSON(`${Theme.routes.cart_url}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
}
const defaultError = () => panel()?.querySelector('[data-gb-cart-error]')?.dataset.message || '';
const notify = (cart) => document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

async function refreshCart() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const target = component();
    if (!target) return;
    const token = {};
    pending.add(token);
    syncControls();
    try {
      await sectionRenderer.renderSection(target.dataset.sectionId, { cache: false, mode: 'hydration', injectStylesheet: true });
      const cart = await cartData();
      needsRefresh = false;
      errorMessage = '';
      settleUndo(cart);
      // The render is complete. Dispatch from the component so its own listener does not morph twice.
      (component() || target).dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'gb-cart-refresh', ajaxCart: cart },
      }) }));
      retrap();
    } catch (error) {
      needsRefresh = true;
      errorMessage = error.message || defaultError();
      throw error;
    } finally {
      pending.delete(token);
      syncControls();
    }
  })().finally(() => { refreshPromise = undefined; });
  return refreshPromise;
}

// Covers the 300 ms interval before Horizon dispatches a deferred request event.
document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const target = event.target.closest?.('.gb-cart cart-items-component');
  if (!target || !event.detail.cartLine) return;
  quantityIntents.add(target);
  addedMessage = '';
  feedbackKeys = [];
  syncControls();
}, true);

// Wait for the response's count/total/quantities to reach the actual hydration target.
// Horizon's empty-to-filled view transition and its no-sections fallback are asynchronous.
async function settleRender(result) {
  await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
  await frame();
  await frame();
  const html = result.detail?.sections?.[component()?.dataset.sectionId];
  const expected = html ? new DOMParser().parseFromString(html, 'text/html') : null;
  const matches = () => {
    const current = panel();
    const count = expected?.querySelector('[ref="cartItemCount"]')?.textContent ?? (result.cart ? String(result.cart.totalQuantity) : undefined);
    if (count !== undefined && current?.querySelector('[ref="cartItemCount"]')?.textContent !== count) return false;
    const summary = expected?.querySelector('[data-gb-cart-summary]');
    if (summary && current?.querySelector('[data-gb-cart-summary]')?.dataset.total !== summary.dataset.total) return false;
    if (!expected && result.cart?.cost?.totalAmount && Number(count) > 0) {
      const total = Math.round(Number(result.cart.cost.totalAmount.amount) * 100);
      if (Number(current?.querySelector('[data-gb-cart-summary]')?.dataset.total) !== total) return false;
    }
    const inputs = [...(expected?.querySelectorAll('input[data-cart-line]') || [])];
    return inputs.every((input) => current?.querySelector(`input[data-cart-line="${input.dataset.cartLine}"]`)?.value === input.value);
  };
  if (matches()) return;
  await new Promise((resolve, reject) => {
    const observer = new MutationObserver(() => { if (matches()) { clearTimeout(timer); observer.disconnect(); resolve(); } });
    const timer = setTimeout(() => { observer.disconnect(); reject(new Error(defaultError())); }, 5000);
    if (panel()) observer.observe(panel(), { childList: true, subtree: true, attributes: true });
    else { clearTimeout(timer); reject(new Error(defaultError())); }
  });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!event.promise || !drawer()) return;
  captureRemoval(event);
  const focus = rememberCartFocus(event);
  const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
  quantityIntents.delete(event.target);
  const token = {};
  pending.add(token);
  syncControls();
  (async () => {
    try {
      const result = await event.promise;
      if (result.detail?.didError) return;
      await settleRender(result);
      if (event.action === 'add') {
        if (sourceModal?.open && sourceModal !== panel()) sourceModal.addEventListener('close', openCart, { once: true });
        else openCart();
      }
      if (event.action === 'add' && result.detail?.source === 'product-form-component') {
        const items = result.detail.items || [];
        const first = items[0];
        if (first) {
          const name = [first.product_title || first.title, first.variant_title].filter((part) => part && part !== 'Default Title').join(' – ');
          const label = panel()?.querySelector('[data-gb-cart-added-status]')?.dataset.label;
          addedMessage = `${label}: ${name}${items.length > 1 ? ` +${items.length - 1}` : ''}`;
          feedbackKeys = items.map((item) => item.key).filter(Boolean);
          panel()?.querySelector('.gb-cart__scroll')?.scrollTo({ top: 0 });
        }
      }
      const cart = result.detail?.ajaxCart || await cartData();
      settleUndo(cart);
      notify(cart);
    } catch (error) {
      needsRefresh = true;
      errorMessage = error.message || defaultError();
      try { await refreshCart(); } catch { /* Retry remains available; checkout remains blocked. */ }
    } finally {
      pending.delete(token);
      for (const target of quantityIntents) if (!target.isConnected) quantityIntents.delete(target);
      syncControls();
      retrap();
      restoreCartFocus(focus);
      flushRefreshRequest();
    }
  })();
}, true);

document.addEventListener(StandardEvents.cartError, (event) => {
  if (!drawer()?.contains(event.target)) return;
  errorMessage = event.error || event.detail?.error || defaultError();
  // A render failure may occur after a resolved event promise, so it needs its own recovery gate.
  if (event.target instanceof Element && drawer().contains(event.target)) {
    needsRefresh = true;
    syncControls();
    refreshCart().catch(() => {});
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' || !event.target.matches?.('.gb-cart__quantity input, [data-gb-cart-redo-input]')) return;
  event.preventDefault();
  event.target.blur();
}, true);
document.addEventListener('submit', (event) => {
  if (!event.target.matches?.('[data-gb-cart-form], [data-gb-cart-checkout-form]')) return;
  const input = document.activeElement;
  const editingQuantity = input?.matches('.gb-cart__quantity input') && drawer()?.contains(input);
  if (editingQuantity) input.blur();
  if (editingQuantity || busy() || adding || needsRefresh || checkingOut) event.preventDefault();
}, true);
// One checkout navigation at a time: a second click before the page leaves is ignored. Set after every other
// listener has run, so a submit someone else cancelled does not block the next one.
const endCheckout = () => { checkingOut = false; clearTimeout(checkoutTimer); };
window.addEventListener('submit', (event) => {
  if (event.defaultPrevented || !event.target.matches?.('[data-gb-cart-checkout-form]')) return;
  checkingOut = true;
  clearTimeout(checkoutTimer);
  checkoutTimer = setTimeout(endCheckout, 10000);
});
window.addEventListener('pageshow', endCheckout);

async function toggleAccessory(button) {
  adding = true;
  addedMessage = '';
  const variantId = button.dataset.variantId;
  const sections = cartSections();
  let update;
  try {
    syncControls();
    const before = await cartData();
    const selected = before.items.filter((item) => item.properties?._gc_upsell === 'sep26' &&
      (String(item.variant_id) === variantId || item.handle === button.dataset.handles));
    if (selected.length) prepareRemoval(before, selected);
    update = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(new CartLinesUpdateEvent({ action: selected.length ? 'remove' : 'add', context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: selected.length ? 0 : 1 }], promise: update.promise }));
    button.classList.add('is-busy');
    const properties = { _gc_upsell: 'sep26' };
    if (button.dataset.bundleHandle) {
      const now = Math.floor(Date.now() / 1000);
      if (now < Number(button.dataset.bundleStart || 0) || (Number(button.dataset.bundleEnd) > 0 && now >= Number(button.dataset.bundleEnd))) throw new Error(defaultError());
      properties._gb_bundle = button.dataset.bundleHandle;
      properties._gb_bundle_variants = button.dataset.bundleVariants;
    }
    const body = selected.length ? { updates: Object.fromEntries(selected.map((item) => [item.key, 0])) } :
      { items: [{ id: Number(variantId), quantity: 1, properties }] };
    const result = await requestJSON(selected.length ? Theme.routes.cart_update_url : Theme.routes.cart_add_url, {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections, sections_url: location.pathname }),
    });
    const cart = await cartData();
    update.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
      items: cart.items, sections: result.sections, itemCount: cart.item_count, source: 'gb-cart', ajaxCart: cart, didError: false,
    } });
  } catch (error) {
    if (update) update.reject(error);
    else { needsRefresh = true; errorMessage = error.message || defaultError(); try { await refreshCart(); } catch {} }
    button.dispatchEvent(new CartErrorEvent({ error: error.message || defaultError(), code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    adding = false;
    button.classList.remove('is-busy');
    syncControls();
    flushRefreshRequest();
  }
}

// Checkout+ (Redo): the box records the choice in a cart attribute; Liquid then shows the Free returns row and puts
// the fee in the total and Checkout button, and Checkout sets the fee line to 1 via /cart/update on the way to checkout.
// Redo's embed strips its fee line from the cart outside its own Checkout+ button, so the line is not kept in the
// cart. Unticking also removes any fee line. Never ticked by default.
const REDO_ATTRIBUTE = '_gb_free_returns';
const isRedoLine = (item, variantId) => item.sku === 'x-redo' || String(item.variant_id) === String(variantId);
async function toggleRedo(input) {
  const checked = input.checked;
  const variantId = input.dataset.redoVariant;
  adding = true;
  addedMessage = '';
  let update;
  try {
    syncControls();
    const before = await cartData();
    const lines = before.items.filter((item) => isRedoLine(item, variantId));
    const body = { attributes: { [REDO_ATTRIBUTE]: checked ? '1' : '' } };
    if (lines.length) body.updates = Object.fromEntries(lines.map((item) => [item.key, 0]));
    update = CartLinesUpdateEvent.createPromise();
    input.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart',
      lines: lines.length ? [{ merchandiseId: variantId, quantity: 0 }] : [], promise: update.promise }));
    const result = await requestJSON(Theme.routes.cart_update_url, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: cartSections(), sections_url: location.pathname }),
    });
    const cart = await cartData();
    update.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
      items: cart.items, sections: result.sections, itemCount: cart.item_count, source: 'gb-cart-redo', ajaxCart: cart, didError: false,
    } });
  } catch (error) {
    if (input.isConnected) input.checked = !checked;
    if (update) update.reject(error);
    else { needsRefresh = true; errorMessage = error.message || defaultError(); try { await refreshCart(); } catch {} }
    input.dispatchEvent(new CartErrorEvent({ error: error.message || defaultError(), code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    adding = false;
    syncControls();
    flushRefreshRequest();
  }
}
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!input.matches?.('[data-gb-cart-redo-input]') || !drawer()?.contains(input)) return;
  // One request at a time: a change during another update is undone, never queued.
  if (busy() || adding || needsRefresh) { input.checked = !input.checked; return; }
  toggleRedo(input);
});

const cartSections = () => [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((node) => node.dataset.sectionId))].join(',');

async function restoreRemoved(button) {
  if (!undoState.length || needsRefresh) return;
  adding = true;
  clearTimeout(undoTimer);
  undoTimer = undefined;
  addedMessage = '';
  const records = undoState;
  let update;
  try {
    syncControls();
    const before = await cartData();
    undoPending = { kind: 'restore', records: records.map((record) => ({ ...record, before: matchingQuantity(before, record.signature) })) };
    update = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: records.map((record) => ({ merchandiseId: String(record.payload.id), quantity: record.payload.quantity })), promise: update.promise }));
    const result = await requestJSON(Theme.routes.cart_add_url, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: records.map((record) => record.payload), sections: cartSections(), sections_url: location.pathname }),
    });
    const cart = await cartData();
    update.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
      items: cart.items, sections: result.sections, itemCount: cart.item_count, source: 'gb-cart-undo', ajaxCart: cart, didError: false,
    } });
  } catch (error) {
    if (update) update.reject(error);
    else {
      needsRefresh = true; errorMessage = error.message || defaultError();
      try { await refreshCart(); } catch { /* Keep the gated retry path. */ }
    }
    button.dispatchEvent(new CartErrorEvent({ error: error.message || defaultError(), code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    adding = false;
    if (undoState.length && !undoTimer) undoTimer = setTimeout(hideUndo, 6000);
    syncControls();
    flushRefreshRequest();
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-gb-cart-upsell], [data-gb-cart-retry], [data-gb-cart-undo-button]');
  if (!button || !drawer()?.contains(button)) return;
  event.preventDefault();
  if (busy() || adding || button.disabled) return;
  if (button.hasAttribute('data-gb-cart-retry')) refreshCart().catch(() => {});
  else if (button.hasAttribute('data-gb-cart-undo-button')) restoreRemoved(button);
  else toggleAccessory(button);
});

function syncOverlay(force = false) {
  const cart = panel();
  if (drawer()?.isOpen && cart) {
    if (lockedPanel && lockedPanel !== cart) unlockScroll(lockedPanel);
    if (lockedPanel !== cart || force) { lockScroll(cart); lockedPanel = cart; }
  } else if (lockedPanel) { unlockScroll(lockedPanel); lockedPanel = undefined; }
}
document.addEventListener('theme-drawer:open', (event) => { if (event.target.id === 'cart-drawer') { syncOverlay(); syncCampaign(); } });
document.addEventListener('theme-drawer:close', (event) => {
  if (event.target.id !== 'cart-drawer') return;
  syncOverlay(); addedMessage = ''; feedbackKeys = []; syncControls();
});
function flushRefreshRequest() {
  if (!refreshRequested || busy() || adding) return;
  refreshRequested = false;
  refreshCart().catch(() => {});
}
for (const name of ['cart:build', 'cart:refresh']) document.addEventListener(name, () => {
  refreshRequested = true;
  flushRefreshRequest();
});
// Horizon changes dialog modality at 990 and releases this owner on the wide side.
// Reassert the overlay lock after its breakpoint handler has run.
desktopQuery.addEventListener('change', () => syncOverlay(true));
document.addEventListener('cart:open', openCart);
document.addEventListener('cart:close', () => drawer()?.close());
for (const name of ['shopify:section:select', 'shopify:block:select', 'shopify:section:deselect', 'shopify:block:deselect']) {
  document.addEventListener(name, (event) => {
    const target = component();
    if (!target) return;
    if (event.detail?.sectionId) {
      if (event.detail.sectionId !== target.dataset.sectionId) return;
    } else if (!event.target.contains?.(drawer()) && !drawer().contains(event.target)) return;
    if (name.endsWith(':deselect')) drawer()?.close();
    else openCart();
  });
}
const observer = new MutationObserver(() => { syncControls(); syncOverlay(); });
function observeCart() {
  observer.disconnect();
  if (drawer()) observer.observe(drawer(), { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  observedSectionId = component()?.dataset.sectionId;
  syncControls(); syncOverlay();
}
document.addEventListener('shopify:section:load', observeCart);
document.addEventListener('shopify:section:unload', (event) => {
  if (event.detail?.sectionId !== observedSectionId) return;
  observer.disconnect();
  clearTimeout(campaignTimer); campaignBoundary = undefined;
  hideUndo(); undoPending = undefined;
  if (lockedPanel) { unlockScroll(lockedPanel); lockedPanel = undefined; }
});
document.addEventListener(ThemeEvents.cartSectionRestored, async (event) => {
  if (!drawer()?.contains(event.target)) return;
  syncControls(); retrap();
  try { notify(await cartData()); } catch { /* Horizon already confirmed the rendered cart. */ }
});
window.addEventListener('pageshow', observeCart);
window.addEventListener('focus', syncCampaign);
document.addEventListener('visibilitychange', () => { if (!document.hidden) syncCampaign(); });
window.addEventListener('pagehide', () => {
  clearTimeout(campaignTimer); campaignBoundary = undefined;
  hideUndo(); undoPending = undefined;
  if (lockedPanel) { unlockScroll(lockedPanel); lockedPanel = undefined; }
  for (const [row, timer] of highlights) { clearTimeout(timer); row.classList.remove('gb-cart__just-added'); }
  highlights.clear();
});
observeCart();
await customElements.whenDefined('theme-drawer');
const url = new URL(location.href);
if (url.searchParams.get('') === 'openCart' && drawer()) {
  openCart();
  url.searchParams.delete('');
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
}
